import React from 'react'
import { makeStyles } from '@mui/styles'
import ThemeInterface from '@/theme-interface'
import DeleteIcon from '@assets/images/icons/trashcan.svg'
import lang from '@/lang'
import { SquareIconButton } from '../../buttons'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import { CssHelper } from '@/modules/base/view/mixin/helpers_mixin'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  container: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: CssHelper.px(14, 16),
    border: theme.border,
    borderRadius: 8,
    background: theme.backgroundColor,
  },
  identity: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minWidth: 0,
  },
  avatar: {
    width: 32,
    height: 32,
    minWidth: 32,
    borderRadius: '50%',
    background: theme.primary,
    color: theme.white,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: theme.fontSizes.small,
    fontWeight: theme.fontWeights.regular,
    textTransform: 'uppercase',
  },
  meta: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  label: {
    fontSize: theme.fontSizes.smedium,
    color: theme.black,
    fontWeight: theme.fontWeights.regular,
    lineHeight: 1.25,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  secondaryLabel: {
    marginTop: 2,
    fontSize: theme.fontSizes.smaller,
    color: theme.textSecondary,
    fontWeight: theme.fontWeights.regular,
    lineHeight: 1.2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  roleBadge: {
    marginTop: 6,
    width: 'fit-content',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    borderRadius: 12,
    background: theme.primary,
    color: theme.white,
    padding: CssHelper.px(2, 8),
    fontSize: theme.fontSizes.tiny,
    fontWeight: theme.fontWeights.regular,
    lineHeight: 1.2,
  },
  roleIcon: {
    fontSize: 12,
    color: theme.white,
  },
  deleteIcon: {
    width: 20,
    height: 20,
  },
}))

interface AccessUserProps {
  type: string;
  subType: string;
  id: string;
  name: string;
  onDelete: (id, type, subType) => void;
  inherited: boolean;
}

const getInitials = (name: string) => {
  const value = String(name || '').trim()
  if (!value) {
    return '?'
  }

  if (value.includes('@')) {
    const [localPart] = value.split('@')
    const parts = localPart.split(/[.\-_ ]+/).filter(Boolean)
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    }
    return localPart.slice(0, 2).toUpperCase()
  }

  const parts = value.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }
  return value.slice(0, 2).toUpperCase()
}

const splitDisplayLabel = (value: string) => {
  const sanitized = String(value || '').trim()
  if (!sanitized) {
    return { primaryLabel: '', secondaryLabel: '' }
  }

  const match = sanitized.match(/^(.*)\s-\s\(([^()]+)\)$/)
  if (match) {
    const primaryLabel = match[1]?.trim() || ''
    const secondaryLabel = match[2]?.trim() || ''
    return {
      primaryLabel: primaryLabel || secondaryLabel,
      secondaryLabel: primaryLabel ? secondaryLabel : '',
    }
  }

  return {
    primaryLabel: sanitized,
    secondaryLabel: '',
  }
}

const AccessUser: React.FC<AccessUserProps> = (props) => {
  const classes = useStyles()
  const isRemovableType = ['editor', 'viewer'].includes(props.type)
  const readOnly = props.type === 'owner' || props.inherited || !isRemovableType
  const accessLabel = `${lang.accessTypes.folder?.[props?.type]?.label}${props.inherited ? ` (${lang.folderAccessPermissions.inherited})` : ''}`
  const { primaryLabel, secondaryLabel } = splitDisplayLabel(props.name)
  const initialsSource = primaryLabel || secondaryLabel

  return (
    <div className={classes.container}>
      <div className={classes.identity}>
        <div className={classes.avatar}>{getInitials(initialsSource)}</div>
        <div className={classes.meta}>
          <div className={classes.label}>{primaryLabel}</div>
          {!!secondaryLabel && <div className={classes.secondaryLabel}>{secondaryLabel}</div>}
          <div className={classes.roleBadge}>
            <PersonOutlineIcon className={classes.roleIcon} />
            <span>{accessLabel}</span>
          </div>
        </div>
      </div>
      {!readOnly && (
        <SquareIconButton
          className={classes.deleteIcon}
          icon={DeleteIcon}
          iconColor='#33406d'
          variant='outline'
          title={lang.deleteButtonLabel}
          onClick={() => props.onDelete(props.id, props.type, props.subType)} />
      )}
    </div>
  )
}

export default AccessUser
